import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UseFormReturn } from "react-hook-form";

import Form from "../../components/base/Form/Form";
import { Button } from "../../components/base/Button";
import { Alert } from "../../components/base/Alert";
import { FormSelect } from "../../components/base/Form/FormSelect";
import { FormTextField } from "../../components/base/Form/FormTextField";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useNotification from "../../components/shared/Notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { errorResolver } from "../../utils/errorResolver";
import useCustomers from "../../data/Customer/useCustomers";
import useCustomerUsers from "../../data/CustomerUser/useCustomerUsers";
import useTicketMutations from "../../data/Ticket/useTicketMutations";
import useApiHelper from "../../hooks/useApiHelper";
import {
  ICreateTicketRequest,
  TicketPriority,
  TicketType,
} from "../../models/Tickets/Ticket";
import useStaffUsers from "../../data/StaffUser/useStaffUsers";
import {
  ticketPriorityLabels,
  ticketPriorityOptions,
  ticketTypeLabels,
  ticketTypeOptions,
} from "./ticketMeta";

interface NewTicketFormFields {
  customerId: string;
  createdByUserId: string;
  assignedToUserId: string;
  subject: string;
  description: string;
  priority: string;
  type: string;
}

export default function NewTicketPage() {
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { customers } = useCustomers();
  const { staffUsers } = useStaffUsers();
  const { create } = useTicketMutations();
  const { postDocs } = useApiHelper();

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const maxAttachmentSizeBytes = 100 * 1024 * 1024;
  const { customerUsers } = useCustomerUsers(selectedCustomerId);

  useEffect(() => {
    setInfo({
      name: "New Ticket",
      desc: "Create a new support ticket.",
      pageTitle: "New Ticket",
    });
  }, [setInfo]);

  const createTicket = (data: NewTicketFormFields) => {
    const oversizedFile = selectedFiles.find(
      (file) => file.size > maxAttachmentSizeBytes,
    );
    if (oversizedFile) {
      addNotification({
        variant: "warn",
        primaryText: `${oversizedFile.name} exceeds the 100MB limit`,
      });
      return;
    }

    const payload: ICreateTicketRequest = {
      customerId: data.customerId,
      createdByUserId: data.createdByUserId,
      subject: data.subject,
      description: data.description,
      assignedToUserId: data.assignedToUserId?.trim() || undefined,
      priority: parseInt(data.priority, 10) as TicketPriority,
      type: parseInt(data.type, 10) as TicketType,
    };

    create.mutate(payload, {
      onSuccess: async (response: any) => {
        const id = response?.data?.id;
        if (!id) {
          navigate("/tickets");
          return;
        }

        try {
          setIsUploadingAttachments(true);
          for (const file of selectedFiles) {
            const formData = new FormData();
            formData.append("file", file);
            await postDocs(`/ticket/${id}/attachments`, formData);
          }

          navigate(`/tickets/${id}`);
        } catch {
          addNotification({
            variant: "error",
            primaryText: "Ticket created but attachment upload failed",
          });
          navigate(`/tickets/${id}`);
        } finally {
          setIsUploadingAttachments(false);
        }
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to create ticket",
        });
      },
    });
  };

  const defaultValues = useMemo<NewTicketFormFields>(
    () => ({
      customerId: "",
      createdByUserId: "",
      assignedToUserId: "",
      subject: "",
      description: "",
      priority: `${TicketPriority.Medium}`,
      type: `${TicketType.Bug}`,
    }),
    [],
  );

  if (customers.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  }

  if (customers.isError) {
    return (
      <Alert
        severity="error"
        title="Error loading customers"
        message={errorResolver(customers.error)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Link
          className="text-sm text-orange-700 hover:text-orange-900"
          to="/tickets"
        >
          {"<- Back to tickets"}
        </Link>
      </div>
      <div className="bg-white rounded shadow p-4">
        {create.isPending ? (
          <div className="py-8">
            <LoadingWheel />
          </div>
        ) : (
          <Form<NewTicketFormFields>
            onSubmit={createTicket}
            defaultValues={defaultValues}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {({ register, setValue }: UseFormReturn<NewTicketFormFields>) => {
              return (
                <>
                  <div className="md:col-span-2 border-b pb-2 mb-1">
                    <p className="text-base font-medium text-gray-600 font-stick">
                      Ticket Details
                    </p>
                  </div>

                  <FormSelect
                    {...register("customerId", {
                      required: true,
                      onChange: (e) => {
                        setSelectedCustomerId(e.target.value || "");
                        setValue("createdByUserId", "");
                      },
                    })}
                    label="Customer"
                  >
                    <option value="">Select customer</option>
                    {(customers.data ?? []).map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </FormSelect>

                  <FormSelect
                    {...register("createdByUserId", { required: true })}
                    label="Created By User"
                    disabled={!selectedCustomerId}
                  >
                    <option value="">Select user</option>
                    {(customerUsers.data ?? []).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.forename} {user.surname} ({user.username})
                      </option>
                    ))}
                  </FormSelect>

                  <FormTextField
                    {...register("subject", { required: true })}
                    label="Subject"
                    placeholder="Issue with part lookup"
                  />

                  <FormSelect
                    {...register("priority", { required: true })}
                    label="Priority"
                  >
                    {ticketPriorityOptions.map((priority) => (
                      <option key={priority} value={priority}>
                        {ticketPriorityLabels[priority]}
                      </option>
                    ))}
                  </FormSelect>

                  <FormSelect
                    {...register("type", { required: true })}
                    label="Type"
                  >
                    {ticketTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {ticketTypeLabels[type]}
                      </option>
                    ))}
                  </FormSelect>

                  <FormSelect
                    {...register("assignedToUserId")}
                    label="Assigned Staff (optional)"
                  >
                    <option value="">Unassigned</option>
                    {(staffUsers.data ?? []).map((staff) => (
                      <option key={staff.userId} value={staff.email}>
                        {staff.displayName || staff.email}
                      </option>
                    ))}
                  </FormSelect>

                  <FormTextField
                    {...register("description", { required: true })}
                    label="Description"
                    placeholder="Describe the issue and requested outcome"
                    containerClassName="md:col-span-2"
                  />

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Attachments
                    </label>
                    <input
                      type="file"
                      multiple
                      className="w-full text-sm text-gray-700"
                      onChange={(e) =>
                        setSelectedFiles(Array.from(e.target.files ?? []))
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Maximum file size: 100MB per attachment.
                    </p>
                    {selectedFiles.length > 0 ? (
                      <ul className="mt-2 space-y-1 text-xs text-gray-600">
                        {selectedFiles.map((file) => (
                          <li key={`${file.name}-${file.lastModified}`}>
                            - {file.name}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>

                  <div className="md:col-span-2 mt-4 pt-4 grid grid-cols-4 gap-4 border-t">
                    <Button type="submit">
                      {create.isPending || isUploadingAttachments
                        ? "Saving..."
                        : "Create Ticket"}
                    </Button>
                    <Button type="reset" variant="outlined">
                      Reset
                    </Button>
                  </div>
                </>
              );
            }}
          </Form>
        )}
      </div>
    </div>
  );
}
